import { getAuth } from 'firebase/auth';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_PROFILE_URL;

class UserService {
  constructor() {
    this.auth = getAuth();
  }

  async getIdToken() {
    const user = this.auth.currentUser;
    if (!user) throw new Error('No user logged in');
    return await user.getIdToken();
  }

  async registerUser(user, referralCode = null) {
    const idToken = await user.getIdToken();
    
    const response = await fetch(`${API_BASE_URL}/users/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({
        uid: user.uid,
        email: user.email,
        full_name: user.displayName || null,
        profile_picture: user.photoURL || null,
        bio: "",  // Bio is not available from Google auth, can be updated later
        referral_code: referralCode,
        goal: 10,  // Default goal value
        score: 0   // Initial score
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Registration failed');
    }

    return await response.json();
  }

  async getUserData(uid) {
    const idToken = await this.getIdToken();
    const response = await fetch(`${API_BASE_URL}/users/${uid}`, {
      headers: {
        'Authorization': `Bearer ${idToken}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user data');
    }

    const data = await response.json();
    return data.data;
  }

  async getUserTasks(uid) {
    const idToken = await this.getIdToken();
    const response = await fetch(`${API_BASE_URL}/users/${uid}/tasks`, {
      headers: {
        'Authorization': `Bearer ${idToken}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch tasks');
    }

    const data = await response.json();
    return data.data;
  }

  async initializeTasks(uid) {
    const idToken = await this.getIdToken();
    const response = await fetch(`${API_BASE_URL}/users/${uid}/tasks/initialize`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to initialize tasks');
    }

    const data = await response.json();
    return data.data;
  }

  async completeTask(uid, taskId) {
    const idToken = await this.getIdToken();
    const response = await fetch(`${API_BASE_URL}/users/${uid}/tasks/${taskId}/complete`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to complete task');
    }

    return await response.json();
  }

  async updateProfile(uid, bio) {
    const idToken = await this.getIdToken();
    const response = await fetch(`${API_BASE_URL}/users/${uid}/profile`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ bio })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update bio');
    }

    const data = await response.json();
    return data.data;
  }

  async getUserSubscription(uid) {
    const idToken = await this.getIdToken();
    const response = await fetch(`${API_BASE_URL}/users/${uid}/subscription`, {
      headers: {
        'Authorization': `Bearer ${idToken}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch subscription details');
    }

    return await response.json();
  }

  async createCheckoutSession(priceId) {
    const idToken = await this.getIdToken();
    const response = await fetch(`${API_BASE_URL}/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({
        priceId,
        successUrl: window.location.href,
        cancelUrl: window.location.href
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create checkout session');
    }

    return await response.json();
  }

  async cancelSubscription(uid, cancelAtPeriodEnd = true) {
    const idToken = await this.getIdToken();
    const response = await fetch(`${API_BASE_URL}/users/${uid}/subscription/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({ cancel_at_period_end: cancelAtPeriodEnd })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to cancel subscription');
    }

    return await response.json();
  }

  async reactivateSubscription(uid) {
    const idToken = await this.getIdToken();
    const response = await fetch(`${API_BASE_URL}/users/${uid}/subscription/reactivate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({})
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to reactivate subscription');
    }

    return await response.json();
  }

  async upgradeSubscription(uid, newPriceId) {
    const idToken = await this.getIdToken();
    const response = await fetch(`${API_BASE_URL}/users/${uid}/subscription/upgrade`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({ new_price_id: newPriceId })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to upgrade subscription');
    }

    return await response.json();
  }

  async getCancellationInfo(uid) {
    const idToken = await this.getIdToken();
    const response = await fetch(`${API_BASE_URL}/users/${uid}/subscription/cancel-info`, {
      headers: {
        'Authorization': `Bearer ${idToken}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch cancellation info');
    }

    return await response.json();
  }

  async uploadAboutResume(uid, aboutMe, resume, filename, parsedResume = undefined) {
    const idToken = await this.getIdToken();
    const body = {
      about_me: aboutMe,
      resume: resume,
      filename: filename
    };
    if (parsedResume !== undefined) {
      body.parsed_resume = parsedResume;
    }
    const response = await fetch(`${API_BASE_URL}/users/${uid}/about_resume`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to upload about me and resume');
    }
    return await response.json();
  }

  async getAboutResume(uid) {
    const idToken = await this.getIdToken();
    const response = await fetch(`${API_BASE_URL}/users/${uid}/about_resume`, {
      headers: {
        'Authorization': `Bearer ${idToken}`
      }
    });
    if (!response.ok) {
      // 404 is not an error, just means no data
      if (response.status === 404) return null;
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch about me and resume');
    }
    return await response.json();
  }

  async uploadWritingSamples(uid, samples) {
    const auth = getAuth();
    const token = await auth.currentUser.getIdToken();
    const response = await axios.post(
      `${API_BASE_URL}/users/${uid}/writing_samples`,
      { samples },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  }

  async getWritingSamples(uid) {
    const auth = getAuth();
    const token = await auth.currentUser.getIdToken();
    const response = await axios.get(`${API_BASE_URL}/users/${uid}/writing_samples`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.writing_samples;
  }

  async saveSearchHistory(uid, searchData, signal) {
    try {
      const token = await this.getIdToken();
      const response = await axios.post(
        `${API_BASE_URL}/users/${uid}/search_history`,
        searchData,
        {
          headers: { Authorization: `Bearer ${token}` },
          signal,
        }
      );
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('Request canceled:', error.message);
      } else {
        console.error('Error saving search history:', error);
        throw error;
      }
    }
  }

  async getSearchHistory(uid, pageSize = 10, cursor = null) {
    try {
      const token = await this.getIdToken();
      
      const params = new URLSearchParams({
          page_size: pageSize,
      });

      if (cursor) {
          params.append('start_after_id', cursor);
      }

      const response = await axios.get(`${API_BASE_URL}/users/${uid}/search_history?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching search history:', error);
      throw error;
    }
  }

  async getSearchHistoryItem(uid, guid) {
    const idToken = await this.getIdToken();
    const response = await fetch(`${API_BASE_URL}/users/${uid}/search_history/${guid}`, {
      headers: {
        'Authorization': `Bearer ${idToken}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch search history item');
    }

    const data = await response.json();
    return data.data;
  }
}

export const userService = new UserService(); 